import React from 'react';

/** Shared IST date + time formatting for all trading screens */

const IST = 'Asia/Kolkata';

/** Today as YYYY-MM-DD in IST */
export function istTodayKey(date = new Date()) {
  return date.toLocaleDateString('en-CA', { timeZone: IST });
}

export function isIstToday(dateKey) {
  if (!dateKey) return false;
  return String(dateKey).slice(0, 10) === istTodayKey();
}

export function formatIstDateTime(value, { includeSeconds = false, fallback = '—' } = {}) {
  if (value == null || value === '') return fallback;
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return fallback;
  return d.toLocaleString('en-IN', {
    timeZone: IST,
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    second: includeSeconds ? '2-digit' : undefined,
    hour12: true
  });
}

export function formatIstDateTimeFromParts(dateKey, timeStr, fallback = '—') {
  if (!dateKey && !timeStr) return fallback;
  if (!dateKey) return formatIstDateTime(timeStr, { fallback });
  const raw = String(timeStr || '').trim();
  if (!raw) return formatIstDateTime(`${dateKey}T12:00:00+05:30`, { fallback });
  const m = raw.match(/(\d{1,2}):(\d{2})(?::(\d{2}))?\s*(am|pm)?/i);
  if (!m) return formatIstDateTime(`${dateKey}T12:00:00+05:30`, { fallback });
  let h = parseInt(m[1], 10);
  const min = parseInt(m[2], 10);
  const sec = parseInt(m[3] || '0', 10);
  const ap = (m[4] || '').toLowerCase();
  if (ap === 'pm' && h < 12) h += 12;
  if (ap === 'am' && h === 12) h = 0;
  const iso = `${dateKey}T${String(h).padStart(2, '0')}:${String(min).padStart(2, '0')}:${String(sec).padStart(2, '0')}+05:30`;
  return formatIstDateTime(iso, { fallback });
}

export function formatIstDateTimeLive(now = new Date()) {
  return formatIstDateTime(now, { includeSeconds: true, fallback: '' });
}

/** Calendar date key (YYYY-MM-DD) → date + time for display. Today uses live clock. */
export function formatIstDateKey(dateKey, defaultTime = '3:30 pm') {
  if (!dateKey) return '—';
  const key = String(dateKey).slice(0, 10);
  if (isIstToday(key)) return formatIstDateTimeLive();
  return formatIstDateTimeFromParts(key, defaultTime);
}

export function formatIstDateRangeLabel(fromDate, toDate) {
  if (!fromDate && !toDate) return 'All dates';
  if (fromDate && toDate && fromDate === toDate) return formatIstDateKey(fromDate);
  if (fromDate && toDate) return `${formatIstDateKey(fromDate)} — ${formatIstDateKey(toDate)}`;
  if (fromDate) return `From ${formatIstDateKey(fromDate)}`;
  if (toDate) return `To ${formatIstDateKey(toDate)}`;
  return 'All dates';
}

/** Activity / log row with separate date + time fields */
export function formatActivityWhen(entry, fallbackDate) {
  if (!entry) return '—';
  if (entry.dateTime) return entry.dateTime;
  const date = entry.date || fallbackDate || istTodayKey();
  return formatIstDateTimeFromParts(date, entry.time || entry.timeLabel);
}

/** Trading action cards — bought/sold stamp with full IST date + time */
export function formatCardOpenedAt(item, fallbackDate) {
  if (!item) return null;
  if (item.dateTime) return item.dateTime;
  if (item.openedAt) return formatIstDateTime(item.openedAt);
  if (item.buyTimeLabel) {
    const raw = String(item.buyTimeLabel).replace(/^Bought\s+/i, '').trim();
    if (/,\s*\d{4}/.test(raw) || /(Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)/i.test(raw)) {
      return raw;
    }
    const dk = item.buyDate || item.date || item.sellDate || fallbackDate || istTodayKey();
    return formatIstDateTimeFromParts(dk, raw);
  }
  return null;
}

export function formatCardSoldAt(item, fallbackDate) {
  if (!item) return null;
  if (item.sellDateTime) return item.sellDateTime;
  const time = item.sellTimeLabel || item.time || item.timeLeft;
  if (!time && !item.sellDate) return null;
  const dk = item.sellDate || item.date || fallbackDate || istTodayKey();
  return formatIstDateTimeFromParts(dk, time);
}

/** Always returns a full IST stamp for trading cards (never empty). */
export function formatCardWhenDisplay(item, prefix = 'Opened') {
  if (!item) return { prefix, stamp: formatIstDateTimeLive() };
  let stamp = item.dateTime || null;
  if (!stamp && item.openedAt) stamp = formatIstDateTime(item.openedAt);
  if (!stamp) stamp = formatCardOpenedAt(item) || formatCardSoldAt(item);
  if (!stamp && item.holdDays != null && Number(item.holdDays) > 0) {
    const ms = Date.now() - Number(item.holdDays) * 86400000;
    stamp = formatIstDateTime(new Date(ms));
  }
  if (!stamp) stamp = formatIstDateTimeLive();
  return { prefix, stamp };
}

export function formatCardWhenLine(item, prefix = 'Opened') {
  const { prefix: p, stamp } = formatCardWhenDisplay(item, prefix);
  return `${p} ${stamp}`;
}

export function useIstClock(intervalMs = 1000) {
  const [label, setLabel] = React.useState(() => formatIstDateTimeLive());
  React.useEffect(() => {
    const tick = () => setLabel(formatIstDateTimeLive());
    tick();
    const id = setInterval(tick, intervalMs);
    return () => clearInterval(id);
  }, [intervalMs]);
  return label;
}
